/**
 * Build My Organizer checklist rows from a procedure intake spec (gm-content / knowledge base).
 */

import { pickEn } from '../../procedures/procedureContent';
import { getStoredProcedureSlug } from '../../procedures/ProcedureContext';
import { getCrewParticipantContext } from '../../utils/participantCrewContext';
import { iso2FromCountryName } from '../../utils/countries';
import filmTvMap from '../../constants/filmTvProcedureMap.json';

const INTAKE_STORAGE_KEY = (slug) => `proc_intake_${slug}`;

/** Retired KB cards still stored on older productions. */
const LEGACY_PROCEDURE_SLUGS = {
  'US-work-p-entertainment': 'US-work-o1b',
};

export function canonicalProcedureSlug(slug) {
  const clean = String(slug || '').trim();
  return LEGACY_PROCEDURE_SLUGS[clean] || clean;
}

/** Slugs that still use the legacy Spain Regularization organizer (hardcoded steps). */
const LEGACY_SPAIN_SLUGS = new Set(['es-regularizacion', '']);

export function persistApplicantProcedureSlug(slug) {
  const clean = canonicalProcedureSlug(slug);
  if (!clean || LEGACY_SPAIN_SLUGS.has(clean)) return;
  try {
    sessionStorage.setItem('mvp_procedure_slug', clean);
    sessionStorage.setItem('mvp_customer_org_procedure_slug', clean);
  } catch {
    /* ignore */
  }
}

export function procedureSlugFromUser(user) {
  const direct = canonicalProcedureSlug(user?.procedure_slug);
  if (direct && !LEGACY_SPAIN_SLUGS.has(direct)) return direct;
  try {
    const stored = JSON.parse(localStorage.getItem('userData') || '{}');
    const fromStored = canonicalProcedureSlug(stored.procedure_slug);
    if (fromStored && !LEGACY_SPAIN_SLUGS.has(fromStored)) return fromStored;
  } catch {
    /* ignore */
  }
  return null;
}

export function persistProcedureSlugFromUser(user) {
  const slug = procedureSlugFromUser(user);
  if (slug) persistApplicantProcedureSlug(slug);
  return slug;
}

export function resolveFilmTvProcedureSlug(originCountry, destinationCountry) {
  const origin = (
    iso2FromCountryName(originCountry)
    || String(originCountry || '').trim().toUpperCase()
  ).slice(0, 2);
  const dest = (
    iso2FromCountryName(destinationCountry)
    || String(destinationCountry || '').trim().toUpperCase()
  ).slice(0, 2);
  if (!dest) return null;
  const routes = filmTvMap.routes || [];
  const route = routes.find(
    (r) => r.origin_country === origin && r.destination_country === dest,
  );
  if (route?.procedure_slug) return canonicalProcedureSlug(route.procedure_slug);
  return canonicalProcedureSlug(filmTvMap.destination_defaults?.[dest]?.procedure_slug) || null;
}

export function resolveApplicantProcedureSlug(opts = {}) {
  const crew = opts.crew ?? getCrewParticipantContext();
  const destName = opts.destinationCountry || opts.destCountryName || '';
  const user = opts.user;
  const stored = getStoredProcedureSlug();
  if (stored && !LEGACY_SPAIN_SLUGS.has(stored)) return stored;
  try {
    const fromCrew = canonicalProcedureSlug(crew?.procedureSlug);
    if (fromCrew && !LEGACY_SPAIN_SLUGS.has(fromCrew)) return fromCrew;
  } catch {
    /* ignore */
  }
  try {
    const u = user || JSON.parse(localStorage.getItem('userData') || '{}');
    const fromUser = canonicalProcedureSlug(u.procedure_slug);
    if (fromUser && !LEGACY_SPAIN_SLUGS.has(fromUser)) return fromUser;
    const filmTv = resolveFilmTvProcedureSlug(
      crew?.country || u.nationality || u.country,
      destName || 'Italy',
    );
    if (filmTv && !LEGACY_SPAIN_SLUGS.has(filmTv)) return filmTv;
  } catch {
    /* ignore */
  }
  return stored && !LEGACY_SPAIN_SLUGS.has(stored) ? stored : null;
}

export function loadIntakeDraftValues(slug) {
  if (!slug) return {};
  try {
    const raw = localStorage.getItem(INTAKE_STORAGE_KEY(slug));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function isIntakeItemFilled(item, values) {
  const raw = values?.[item?.id];
  if (raw == null) return false;
  if (typeof raw === 'string') return raw.trim() !== '';
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'object') {
    if (Array.isArray(raw)) return raw.length > 0;
    return Object.keys(raw).length > 0;
  }
  return Boolean(String(raw).trim());
}

/**
 * @returns {{
 *   infoSections: Array<{ groupId: string, label: string, done: boolean, requiredCount: number, filledRequired: number, intakeStep: number }>,
 *   documentSection: { label: string, items: Array<{ id: string, label: string, done: boolean, optional: boolean }>, done: boolean, intakeStep: number } | null,
 *   signIntakeStep: number,
 *   completedCount: number,
 *   totalCount: number,
 * }}
 */
export function buildOrganizerFromIntake(intake, values = {}, lang = 'en', { signDone = false } = {}) {
  const items = (intake?.items || []).filter((i) => i.collect !== false);
  const displayGroupsMeta = intake?.display_groups || {};
  const infoItems = items.filter((i) => i.kind === 'info');
  const docItems = items.filter((i) => i.kind === 'document');

  const pickLabel = (field) => {
    if (field == null) return '';
    if (typeof field === 'string') return field;
    if (field.text && typeof field.text === 'object') {
      return field.text[lang] || field.text.en || field.text.es || pickEn(field.text);
    }
    return field[lang] || field.en || pickEn(field);
  };

  const groupOrder = [];
  const seen = new Set();
  infoItems.forEach((item) => {
    const g = item.display_group || '__other__';
    if (!seen.has(g)) {
      seen.add(g);
      groupOrder.push(g);
    }
  });

  const infoSections = groupOrder.map((g, idx) => {
    const groupItems = infoItems.filter((i) => (i.display_group || '__other__') === g);
    const required = groupItems.filter((i) => i.required === 'required');
    const filledRequired = required.filter((i) => isIntakeItemFilled(i, values)).length;
    const allRequiredDone = required.length === 0
      ? groupItems.every((i) => isIntakeItemFilled(i, values))
      : filledRequired >= required.length;
    return {
      groupId: g,
      label: pickLabel(displayGroupsMeta[g]?.text) || g.replace(/[._]/g, ' '),
      done: allRequiredDone && (required.length > 0 || groupItems.some((i) => isIntakeItemFilled(i, values))),
      requiredCount: required.length || groupItems.length,
      filledRequired: required.length ? filledRequired : groupItems.filter((i) => isIntakeItemFilled(i, values)).length,
      intakeStep: idx + 1, // ProcedureIntakePage: 0=landing, 1..N=info groups
    };
  });

  // Mark section done if all required filled (even when empty optional-only group)
  infoSections.forEach((section) => {
    const groupItems = infoItems.filter((i) => (i.display_group || '__other__') === section.groupId);
    const required = groupItems.filter((i) => i.required === 'required');
    if (required.length === 0) {
      section.done = groupItems.every((i) => i.required === 'optional' || isIntakeItemFilled(i, values));
    } else {
      section.done = required.every((i) => isIntakeItemFilled(i, values));
    }
  });

  let documentSection = null;
  if (docItems.length) {
    const docRows = docItems.map((d) => ({
      id: d.id,
      label: pickLabel(d.prompt) || d.id,
      done: isIntakeItemFilled(d, values),
      optional: d.required === 'optional',
    }));
    const requiredDocs = docRows.filter((d) => !d.optional);
    documentSection = {
      label: 'Documents',
      items: docRows,
      done: requiredDocs.length === 0
        ? docRows.every((d) => d.done)
        : requiredDocs.every((d) => d.done),
      intakeStep: infoSections.length + 1,
    };
  }

  const completedInfo = infoSections.filter((s) => s.done).length;
  const completedDocs = documentSection && documentSection.done ? 1 : 0;
  const totalInfo = infoSections.length;
  const totalDocs = documentSection ? 1 : 0;
  const signIntakeStep = totalInfo + totalDocs + 1;

  return {
    infoSections,
    documentSection,
    signIntakeStep,
    completedCount: completedInfo + completedDocs + (signDone ? 1 : 0),
    totalCount: totalInfo + totalDocs + 1,
  };
}
